'use client'

import { Fragment, useCallback, useEffect, useRef } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import SpotList from 'component/page/spot/spot-list'
import { ScrollSnapItem } from 'component/ui/scroll-area'
import { NO_MORE_DATA, TEMP_USER_ID } from 'constant'
import { useLocale } from 'next-intl'
import { fetchSpotsFeed } from 'server-actions/fetch-spots'
import { useInitialSpotIdsStore } from 'store/global/initial-spot-ids'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import type { IInStreamSpotReqV2, IInStreamSpotV2 } from 'type/feed'
import type { ISpotV2WithFullContent } from 'type/spot'
import ScrollSpacer from '../scroll-spacer'
import Spot from '../spot'
import useSpots from './use-spots'

interface ISpotLoader {
  spot: ISpotV2WithFullContent
}
const SpotsLoader = (props: ISpotLoader) => {
  const { spot } = props
  const spotIdsRef = useRef(useInitialSpotIdsStore.getState().initialSpotIds)
  const { spotList, loading } = useSpots(spotIdsRef.current)
  const locale = useLocale()
  const allSpotsRef = useRef<(ISpotV2WithFullContent | IInStreamSpotV2)[]>([])
  const ipLocation = useIpLocationStore(state => state.ipLocation)
  const userSession = useSessionStore(state => state.session)

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['spot/place', spot.id],
    initialPageParam: '',
    queryFn: async ({ pageParam = '' }) => {
      const { lat, lng } = ipLocation?.ip_geo_point || { lat: 0, lng: 0 }

      let user_coordinate = {
        lat: `${lat}`,
        lng: `${lng}`
      }
      const userId = userSession.guest ? TEMP_USER_ID : userSession.user_id

      const req: IInStreamSpotReqV2 = {
        info: {
          country: ipLocation?.country_code || '',
          language: locale,
          user_id: userId,
          user_coordinate
        },
        location_id: '',
        location_picker: null,
        is_change_location: false,
        session_id: pageParam,
        is_refresh: false
      }

      const res = await fetchSpotsFeed(req)

      return res
    },
    select: data => data.pages.flatMap(page => page.spots),
    getNextPageParam: lastPage => {
      if (
        lastPage.session?.id === NO_MORE_DATA ||
        !lastPage.session?.id ||
        !lastPage.spots.length
      ) {
        return undefined
      }
      return lastPage.session?.id || ''
    },
    enabled: !loading
  })

  const allSpots = [...spotList, ...(data ?? [])]

  allSpotsRef.current = [spot, ...allSpots]

  const handleIntersect = useCallback(
    (index: number) => {
      window.history.replaceState(
        null,
        '',
        '/spot/' + allSpotsRef.current[index].id
      )
      if (index >= allSpots.length - 3) {
        hasNextPage && fetchNextPage()
      }
    },
    [allSpots.length, fetchNextPage, hasNextPage]
  )

  useEffect(() => {
    if (!loading && spotList.length < 3) {
      fetchNextPage()
    }
  }, [spotList.length, fetchNextPage, loading])

  return (
    <SpotList>
      <Fragment key={spot.id}>
        <ScrollSnapItem>
          <Spot spot={spot} index={0} onIntersect={handleIntersect} />
        </ScrollSnapItem>
        <ScrollSpacer />
      </Fragment>

      {allSpots.map((spot, i) => (
        <Fragment key={spot.id}>
          <ScrollSnapItem>
            <Spot spot={spot} index={i + 1} onIntersect={handleIntersect} />
          </ScrollSnapItem>
          <ScrollSpacer />
        </Fragment>
      ))}
    </SpotList>
  )
}

export default SpotsLoader
